import {
  Answer,
  FormItem,
  ItemType,
  ResponseSubject,
  ResponseSubjectType,
} from "../../types";

/**
 * Response coaching (FL-333 Phase 1) — deterministic advisory checks run
 * entirely client-side. Advisories are informational only: they never gate
 * completion or submission. Model-backed checks (sentiment/score mismatch,
 * professionalism, vagueness) are a later phase — see
 * docs/response-coaching-architecture.md.
 */

export interface Advisory {
  /** Stable rule id — also the dismissal key. */
  id: string;
  /** i18n key under `responseCoaching.` for the student-facing message. */
  messageKey: string;
}

// Thresholds are first guesses, kept as named constants so instructor
// feedback can retune them in one place (each is pinned by a unit test).
export const SAME_SCORE_MIN_SUBJECTS = 2;
export const SAME_SCORE_MIN_ITEMS = 3;
export const SAME_SCORE_UNIFORM_RATIO = 0.8;
export const WITHIN_SUBJECT_MIN_ANSWERS = 4;
export const SHORT_ANSWER_MAX_WORDS = 4;
// Below this word count, identical short answers ("n/a", "none") across
// teammates are expected, not copy-paste — don't flag them.
export const DUPLICATE_TEXT_MIN_WORDS = 4;

const hasValue = (answer: Answer) =>
  answer.value !== null && answer.value !== undefined;

const wordCount = (text: string) =>
  text.trim() === "" ? 0 : text.trim().split(/\s+/).length;

const normalizeText = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/** Shape of `checkResponseCoaching.advisories` (Phase 2, model-backed). */
export interface CoachingAdvisoryResult {
  ruleId: string;
  itemId: string;
  confidence: number;
}

const MODEL_RULE_MESSAGE_KEYS: Record<string, string> = {
  "score-text-mismatch": "responseCoaching.scoreTextMismatch",
  unprofessional: "responseCoaching.unprofessional",
  vague: "responseCoaching.vague",
  "not-actionable": "responseCoaching.notActionable",
  irrelevant: "responseCoaching.irrelevant",
  "self-contradictory": "responseCoaching.selfContradictory",
};

/** One flagged dimension on one text answer, rendered inline under it. */
export interface ItemAdvisory {
  ruleId: string;
  messageKey: string;
  /** Higher = shown first — the most confidently-detected problem is the
   *  "weakest dimension" the student should fix first. Model verdicts use
   *  their real confidence (0..1); deterministic rules use
   *  DETERMINISTIC_CONFIDENCE so a near-certain model verdict (e.g.
   *  unprofessional 0.99) still outranks the generic "too short" hint. */
  confidence: number;
}

export const DETERMINISTIC_CONFIDENCE = 0.85;

/**
 * Group server verdicts by the text answer that tripped them, strongest
 * dimension first, so each hint renders under the field it's about.
 * Unknown rule ids from a newer backend are ignored rather than rendered
 * broken.
 */
export function itemAdvisoriesFromCoachingResult(
  results: CoachingAdvisoryResult[],
): Record<string, ItemAdvisory[]> {
  const byItem: Record<string, ItemAdvisory[]> = {};
  for (const result of results) {
    const messageKey = MODEL_RULE_MESSAGE_KEYS[result.ruleId];
    if (!messageKey) continue;
    const list = (byItem[result.itemId] ??= []);
    if (list.some((a) => a.ruleId === result.ruleId)) continue;
    list.push({
      ruleId: result.ruleId,
      messageKey,
      confidence: result.confidence,
    });
  }
  for (const list of Object.values(byItem)) {
    list.sort((a, b) => b.confidence - a.confidence);
  }
  return byItem;
}

/**
 * Deterministic per-item checks for ONE subject — instant, client-side,
 * recomputed on every keystroke (unlike the model checks, which run on
 * typing-idle/blur). Currently just the very-short-answer floor of "vague";
 * skipped when the model already flagged vague on the same item so the
 * student doesn't read the same advice twice.
 */
export function detectItemAdvisories(
  subject: ResponseSubject,
  formItems: FormItem[],
  modelAdvisories: Record<string, ItemAdvisory[]> = {},
): Record<string, ItemAdvisory[]> {
  const textItemIds = new Set(
    formItems
      .filter((item) => item.itemType === ItemType.Text)
      .map((item) => item.id),
  );
  const byItem: Record<string, ItemAdvisory[]> = {};
  for (const answer of subject.answers) {
    if (!textItemIds.has(answer.itemId)) continue;
    if (typeof answer.text !== "string") continue;
    const words = wordCount(answer.text);
    const modelFlaggedVague = (modelAdvisories[answer.itemId] ?? []).some(
      (a) => a.ruleId === "vague",
    );
    if (words > 0 && words <= SHORT_ANSWER_MAX_WORDS && !modelFlaggedVague) {
      byItem[answer.itemId] = [
        {
          ruleId: "very-short-written",
          messageKey: "responseCoaching.veryShortWritten",
          confidence: DETERMINISTIC_CONFIDENCE,
        },
      ];
    }
  }
  return byItem;
}

/** detectItemAdvisories + model verdicts merged per item, strongest first. */
export function mergeItemAdvisories(
  deterministic: Record<string, ItemAdvisory[]>,
  model: Record<string, ItemAdvisory[]>,
): Record<string, ItemAdvisory[]> {
  const merged: Record<string, ItemAdvisory[]> = {};
  for (const itemId of new Set([
    ...Object.keys(deterministic),
    ...Object.keys(model),
  ])) {
    merged[itemId] = [
      ...(model[itemId] ?? []),
      ...(deterministic[itemId] ?? []),
    ].sort((a, b) => b.confidence - a.confidence);
  }
  return merged;
}

export function detectAdvisories(
  responseSubjects: ResponseSubject[],
  formItems: FormItem[],
): Advisory[] {
  const advisories: Advisory[] = [];

  // General Reflection isn't about a person, so it never participates in
  // straight-lining comparisons; Points items have their own min-variance
  // guidance in ResponsePoints and are excluded to avoid double-warning.
  const rated = responseSubjects.filter(
    (rs) => rs.subjectType !== ResponseSubjectType.General,
  );
  const choiceItemIds = formItems
    .filter((item) => item.itemType === ItemType.Choice)
    .map((item) => item.id);
  const textItemIds = new Set(
    formItems
      .filter((item) => item.itemType === ItemType.Text)
      .map((item) => item.id),
  );

  // Rule: same scores across subjects ("gave everyone the same ratings").
  let crossSubjectFired = false;
  if (rated.length >= SAME_SCORE_MIN_SUBJECTS) {
    let eligibleItems = 0;
    let uniformItems = 0;
    for (const itemId of choiceItemIds) {
      const values = rated
        .map((rs) => rs.answers.find((a) => a.itemId === itemId))
        .filter((a): a is Answer => a !== undefined && hasValue(a))
        .map((a) => a.value);
      if (values.length < SAME_SCORE_MIN_SUBJECTS) continue;
      eligibleItems += 1;
      if (values.every((v) => v === values[0])) uniformItems += 1;
    }
    if (
      eligibleItems >= SAME_SCORE_MIN_ITEMS &&
      uniformItems / eligibleItems >= SAME_SCORE_UNIFORM_RATIO
    ) {
      crossSubjectFired = true;
      advisories.push({
        id: "same-scores-across-subjects",
        messageKey: "responseCoaching.sameScoresAcrossSubjects",
      });
    }
  }

  // Rule: same score on every question for one subject. Subsumed by the
  // cross-subject rule when that fired — showing both would double-warn
  // about the same behaviour.
  if (!crossSubjectFired) {
    const straightLined = rated.some((rs) => {
      const values = rs.answers
        .filter((a) => choiceItemIds.includes(a.itemId) && hasValue(a))
        .map((a) => a.value);
      return (
        values.length >= WITHIN_SUBJECT_MIN_ANSWERS &&
        values.every((v) => v === values[0])
      );
    });
    if (straightLined) {
      advisories.push({
        id: "same-score-within-subject",
        messageKey: "responseCoaching.sameScoreWithinSubject",
      });
    }
  }

  // (very-short-written moved to detectItemAdvisories — it's about one
  // specific answer, so it renders inline under that field now.)

  // Rule: the same written answer reused across teammates (copy-paste).
  // We hold every subject's text, so this is a free client-side check —
  // the text-uniqueness dimension FeedbackFruits flags. Completed subjects
  // only, and non-trivial length, so transient mid-typing matches and
  // legitimate "n/a" repeats don't fire.
  const normalizedTexts = rated
    .filter((rs) => rs.completed)
    .flatMap((rs) =>
      rs.answers
        .filter(
          (a) =>
            textItemIds.has(a.itemId) &&
            typeof a.text === "string" &&
            wordCount(a.text) >= DUPLICATE_TEXT_MIN_WORDS,
        )
        .map((a) => normalizeText(a.text as string)),
    );
  const seen = new Set<string>();
  let hasDuplicate = false;
  for (const text of normalizedTexts) {
    if (seen.has(text)) {
      hasDuplicate = true;
      break;
    }
    seen.add(text);
  }
  if (hasDuplicate) {
    advisories.push({
      id: "duplicate-written-across-subjects",
      messageKey: "responseCoaching.duplicateWritten",
    });
  }

  return advisories;
}
